// @flow

/* ButtonGroup is an aesthetically pleasing group of buttons.
 *
 * The class requires these properties:
 *   buttons - an array of objects with keys:
 *     "value": this is the value returned when the button is selected
 *     "content": this is the JSX shown within the button, typically a string
 *         that gets rendered as the button's display text
 *     "title": this is the title-text shown on hover
 *   onChange - a function that is provided with the updated value
 *     (which it then is responsible for updating)
 *
 * The class has these optional properties:
 *   value - the initial value of the button selected, defaults to null.
 *   allowEmpty - if false, exactly one button _must_ be selected; otherwise
 *     it defaults to true and _at most_ one button (0 or 1) may be selected.
 *
 * Requires stylesheets/perseus-admin-package/editor.less to look nice.
 *
 * Stolen from https://raw.githubusercontent.com/Khan/react-components/master/js/button-group.jsx
 */

import React from 'react'
import styles from '../styles'

type PropTypes = {
    allowEmpty: boolean,
    buttons: Array<{
        value: any,
        content: any,
        title: string,
    }>,
    enabled: boolean,
    onChange: Function,
    theme: styles,
    value: any,
}

function ButtonGroup(props: PropTypes) {
    const toggleSelect = (newValue) => {
        if (props.enabled) {
            if (props.allowEmpty) {
                // Select the new button or unselect if it's already selected
                props.onChange(props.value !== newValue ? newValue : null)
            } else {
                props.onChange(newValue)
            }
        }
    }

    const buttonStyles = [props.theme.button, props.theme.buttonStyle]

    const buttons = props.buttons.map((button, i) => (
        <button
            title={button.title}
            type="button"
            id={i}
            key={button.value}
            className={`${buttonStyles.join(' ')} ${button.value === props.value && props.theme.selectedStyle}`}
            onClick={() => toggleSelect(button.value)}
        >
            {button.content || button.value.toString()}
        </button>
    ))

    const outerStyle = {
        display: 'inline-block',
    }

    return (
        <div style={outerStyle}>
            {buttons}
        </div>
    )
}

ButtonGroup.defaultProps = {
    allowEmpty: true,
    enabled: true,
    value: null,

}


export default ButtonGroup
